/**
 * ── ModuleRoute ──
 * Route-level gating component. Wraps a page component and checks
 * module access before rendering. Shows upgrade/unauthorized state
 * instead of breaking routing.
 *
 * Usage in router:
 *   <Route path="/sponsorship/*" element={
 *     <ModuleRoute module="sponsorships">
 *       <SponsorshipPage />
 *     </ModuleRoute>
 *   } />
 */

import { useModuleAccess } from "@/hooks/useModuleAccess";
import EntitlementGate from "@/components/EntitlementGate";

interface ModuleRouteProps {
  /** Module key from the registry */
  module: string;
  /** Page component(s) to render when access is granted */
  children: React.ReactNode;
  /** Custom label for the locked/upgrade state */
  label?: string;
}

const ModuleRoute = ({ module, children, label }: ModuleRouteProps) => {
  const { isEligible, definition } = useModuleAccess(module);

  // If module is not eligible for this tenant type, show unauthorized
  if (!isEligible) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center min-h-[60vh]">
        <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-5">
          <span className="text-2xl text-muted-foreground">🔒</span>
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-2">
          {definition?.label ?? label ?? "Feature"} — Not Available
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          This feature is not available for your account type. Contact your administrator for more information.
        </p>
      </div>
    );
  }

  // Delegate to EntitlementGate for access/upgrade/locked states
  return (
    <EntitlementGate module={module} label={label ?? definition?.label}>
      {children}
    </EntitlementGate>
  );
};

export default ModuleRoute;
